/**
 * Which design software a project was made with, from the files themselves.
 *
 * Three signals, strongest first:
 *   1. a parsed CAM design file — its format IS the software (.crv => Vectric)
 *   2. a project file's extension — .sldprt => Solidworks, and so on
 *   3. the g-code header's own CAM-software comment
 *
 * This used to live on the backend as well (post.utils.ts's
 * FILE_EXTENSION_TO_SOFTWARE_MAP), running again on every upload and delete.
 * The wizard already holds the files and shows the answer for the user to
 * correct, so a second detector server-side could only ever re-add software
 * the user had just removed.
 */
import { ISoftwareUsed } from '@/redux/post/post.d';
import { CamExtractedData } from '@/workers/camTypes';
import { V2File } from '../v2Upload.d';

export const SOFTWARE_BY_FORMAT: Record<CamExtractedData['format'], string> = {
  crv: ISoftwareUsed.VECTRIC,
  art: ISoftwareUsed.CARVECO,
  v3m: ISoftwareUsed.VECTRIC,
};

/** Design/project file extensions that name their software outright. G-code
 * extensions are deliberately absent — every package writes .nc, so only the
 * header inside it can say which one did. */
const BY_EXTENSION: Record<string, string> = {
  art: ISoftwareUsed.CARVECO,
  crv: ISoftwareUsed.VECTRIC,
  crv3d: ISoftwareUsed.VECTRIC,
  v3d: ISoftwareUsed.VECTRIC,
  f3d: ISoftwareUsed.FUSION_360,
  f3z: ISoftwareUsed.FUSION_360,
  c2d: ISoftwareUsed.CARBIDE_CREATE,
  easel: ISoftwareUsed.EASEL,
  sldprt: ISoftwareUsed.SOLIDWORKS,
  sldasm: ISoftwareUsed.SOLIDWORKS,
  slddrw: ISoftwareUsed.SOLIDWORKS,
  mcf: ISoftwareUsed.MESHCAM,
  ezd: ISoftwareUsed.EZ_CAD,
  fcstd: ISoftwareUsed.FREECAM,
  e10: ISoftwareUsed.ESTLCAM,
  lbrn: ISoftwareUsed.LIGHTBURN,
  lbrn2: ISoftwareUsed.LIGHTBURN,
};

/** The software a g-code header names, as a member of the fixed list — the
 * multi-select cannot show a free-text value. */
const BY_HEADER: Array<[RegExp, string]> = [
  [/carveco|artcam/i, ISoftwareUsed.CARVECO],
  [/vectric|vcarve|aspire|cut2d|cut3d|photovcarve/i, ISoftwareUsed.VECTRIC],
  [/fusion\s*360|autodesk|hsmworks/i, ISoftwareUsed.FUSION_360],
  [/carbide\s*create|carbide3d/i, ISoftwareUsed.CARBIDE_CREATE],
  [/\beasel\b|inventables/i, ISoftwareUsed.EASEL],
  [/solidworks|solid\s*works/i, ISoftwareUsed.SOLIDWORKS],
  [/meshcam/i, ISoftwareUsed.MESHCAM],
  [/ez\s*-?\s*cad|ezcad/i, ISoftwareUsed.EZ_CAD],
  [/freecam|freecad/i, ISoftwareUsed.FREECAM],
  [/estlcam/i, ISoftwareUsed.ESTLCAM],
  [/lightburn/i, ISoftwareUsed.LIGHTBURN],
];

export function softwareFromExtension(fileName: string): string | null {
  const ext = fileName.toLowerCase().split('.').pop() ?? '';
  return BY_EXTENSION[ext] ?? null;
}

export function softwareFromHeader(camSoftware: string | null): string | null {
  if (!camSoftware) return null;
  const hit = BY_HEADER.find(([re]) => re.test(camSoftware));
  return hit ? hit[1] : null;
}

/**
 * Every software the project's own files name, strongest signal first.
 *
 * A project drawn in one package and posted through another genuinely used
 * both — the ten-project audit set lists Vectric and Carveco together — so
 * this collects rather than picks.
 */
export function detectSoftware(
  files: V2File[],
  cam: CamExtractedData | null,
  /** The wizard's top-level parse. Every file's own `extracted` is read too —
   * that one is `parsed[0]`, alphabetically the Buildbotics export, whose post
   * processor writes no CAM name at all. */
  gcodeHeader: string | null = null
): string[] {
  const named = [
    ...(cam ? [SOFTWARE_BY_FORMAT[cam.format]] : []),
    ...files.map((f) => softwareFromExtension(f.name)),
    ...files.map((f) => softwareFromHeader(f.extracted?.camSoftware ?? null)),
    softwareFromHeader(gcodeHeader),
  ];
  return [...new Set(named.filter((s): s is string => Boolean(s)))];
}
